import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const techData = [
  {
    title: "Xiamen SANAN LED čip",
    tag: "LED",
    desc: "Prvotřídní polovodiče od světového lídra zaručují vysokou účinnost a stabilní světelný tok. Čipy jsou navrženy pro dlouhodobý provoz s minimálním úbytkem jasu v čase.",
    image: "https://www.lightwell.cz/media/technologie/sanan-cip.svg"
  },
  {
    title: "PMMA optická čočka",
    tag: "LED",
    desc: "Speciální optika s texturou včelí plástve (honeycomb) rozptyluje světlo do extrémně širokého úhlu. To umožňuje rovnoměrné prozáření plochy i při malé hloubce boxu.",
    image: "https://www.lightwell.cz/media/technologie/pmma-cocka.svg"
  },
  {
    title: "Úhel vyzařování",
    tag: "LED",
    desc: "Extrémně široký vyzařovací úhel je klíčem k úspoře. Umožňuje umístit LED bary s většími rozestupy při zachování perfektní homogenity světla na čelní ploše reklamy.",
    image: "https://www.lightwell.cz/media/technologie/uhel-paprsku-170.svg"
  },
  {
    title: "Hliníkový pás – nosič",
    tag: "LED",
    desc: "Zajišťuje efektivní odvod tepla od LED čipů, což je klíčové pro jejich stabilitu a dlouhou životnost. Hliníková konstrukce nabízí vysokou tepelnou vodivost.",
    image: "https://www.lightwell.cz/media/technologie/hlinikova-lista.svg"
  },
  {
    title: "Bezpečná ochrana proudu",
    tag: "LED",
    desc: "Integrované ochranné prvky v každém modulu zajišťují, že každá dioda pracuje v optimálním režimu bez rizika přehřátí nebo poškození napěťovými špičkami.",
    image: "https://www.lightwell.cz/media/technologie/ochrana-proudu.svg"
  },
  {
    title: "Rovnoměrné prozáření",
    tag: "LED",
    desc: "Díky kombinaci širokého vyzařovacího úhlu a honeycomb optiky je celá plocha prosvětlena dokonale rovnoměrně bez vzniku ostrých světelných bodů nebo tmavých pruhů.",
    image: "https://www.lightwell.cz/media/technologie/rovnomerne-svetlo.svg"
  },
  {
    title: "Teplota barev a věrnost podání",
    tag: "LED",
    desc: "Věrnost podání barev (CRI) a správná teplota chromatičnosti určují, jak bude vaše reklama působit. Naše systémy jsou laděny pro věrnou reprodukci grafiky.",
    image: "https://www.lightwell.cz/media/technologie/teplota-barev.svg"
  },
  {
    title: "Regulátor intenzity po skocích",
    tag: "POWER",
    desc: "DIP přepínač přímo na těle zdroje. Řada HHE nastavuje jas ve čtyřech krocích (40 / 60 / 80 / 100 %), řada HHX přepíná mezi 100 % a 50 %. Žádná externí elektronika navíc.",
    image: "https://www.lightwell.cz/media/technologie/dip-stmivani.svg"
  },
  {
    title: "Postupný náběh výkonu",
    tag: "POWER",
    desc: "Jas najíždí na 100 % plynule, přibližně 15 sekund, ne skokem. Eliminuje proudové špičky při studeném startu a nezatěžuje tolik jistič.",
    image: "https://www.lightwell.cz/media/technologie/postupny-nabeh.svg"
  },
  {
    title: "Rychlospojovací svorky",
    tag: "POWER",
    desc: "Lisovací terminál bez šroubování - vodič se jen zacvakne. Výrazně rychlejší montáž než u klasických šroubových svorek.",
    image: "https://www.lightwell.cz/media/technologie/rychlospojovaci-svorky.svg"
  }
];

const TechDeepDive = () => {
  const [activeTag, setActiveTag] = useState('VŠE');
  const uniqueTags = [...new Set(techData.map(item => item.tag))];
  const filteredData = activeTag === 'VŠE'
    ? techData
    : techData.filter(item => item.tag === activeTag);

  return (
    <section id="technologie" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 italic tracking-tighter uppercase">
            Technologické principy
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Využíváme pouze certifikované komponenty a fyzikální zákony optiky pro dokonalý výsledek.
          </p>
        </div>

        {uniqueTags.length > 1 && (
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            <button
              onClick={() => setActiveTag('VŠE')}
              className={`px-8 py-3 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all ${
                activeTag === 'VŠE'
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              VŠE
            </button>
            {uniqueTags.map(tag => (
              <button
                key={tag}
                onClick={() => setActiveTag(tag)}
                className={`px-8 py-3 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all ${
                  activeTag === tag
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="popLayout">
            {filteredData.map(item => (
              <motion.div
                key={item.title}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col h-full hover:shadow-xl transition-all duration-500 overflow-hidden"
              >
                <div className="aspect-[16/9] w-full bg-white border-b border-gray-50">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-8 flex flex-col flex-grow">
                  <div className="mb-4">
                    <span className="bg-gray-100 text-gray-500 px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest">
                      {item.tag}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 leading-tight">
                    {item.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed flex-grow">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        <div className="mt-24 pt-24 border-t border-gray-100">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-12 italic tracking-tighter uppercase text-center">
            Žebřík a rozteč
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
              <h3 className="p-8 pb-0 text-lg font-bold uppercase tracking-widest text-blue-600">
                IP20 Varianta
              </h3>
              <img
                src="https://www.lightwell.cz/media/produkty/zebrik-ip20.svg"
                alt="Diagram žebříku IP20"
                className="w-full h-auto"
              />
            </div>
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
              <h3 className="p-8 pb-0 text-lg font-bold uppercase tracking-widest text-gray-900">
                IP65 Varianta
              </h3>
              <img
                src="https://www.lightwell.cz/media/produkty/zebrik-ip65.svg"
                alt="Diagram žebříku IP65"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechDeepDive;